import React, { useState } from "react";
import { useDemoMode } from "@/hooks/useDemoMode";
import { mockAuth } from "@/mock";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import { ExternalLink, Lock, Shield } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const API = process.env.REACT_APP_BACKEND_URL;

export const LoginScreen = ({ onSignedIn }) => {
  const [role, setRole] = useState("consultant");
  const [name, setName] = useState("Alex Consultant");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const isDemo = useDemoMode();

  const handleEmailLogin = async () => {
    if (!email || !password) return;
    setLoginLoading(true);
    setLoginError("");
    try {
      const resp = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || "Invalid credentials");
      }
      const user = await resp.json();
      if (user.must_change_password || user.password_reset_required) {
        toast.success("Signed in — please set a new password");
        window.location.assign("/change-password");
        return;
      }
      if ((user.role || "").toLowerCase() === "sales") {
        toast.success("Signed in");
        window.location.assign("/sales");
        return;
      }
      onSignedIn({
        user: {
          id: user.user_id,
          full_name: user.name,
          email: user.email,
          role: user.role,
          picture: user.picture || "",
        },
      });
      toast.success("Signed in");
    } catch (e) {
      setLoginError(e.message);
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="login-screen">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_20%_0%,rgba(140,170,220,0.07),transparent_50%),radial-gradient(circle_at_60%_10%,rgba(255,122,51,0.03),transparent_58%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.05),transparent_60%)]" />

      <div className="mx-auto flex min-h-screen max-w-6xl items-center px-4 py-14 sm:px-6">
        <div className="grid w-full gap-8 md:grid-cols-2 md:items-center">
          <div>
            {isDemo && (
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-foreground/5 px-3 py-1 text-xs text-foreground/70 backdrop-blur-xl" data-testid="demo-mode-badge">
                Demo Mode &middot; AUTH is <span className="text-foreground">MOCKED</span>
              </div>
            )}
            <h1 className="mt-4 text-4xl font-semibold tracking-tight text-foreground">
              Sign in to the client portal
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground/65">
              {isDemo
                ? 'Demo mode: Use the mock sign-in below to preview different roles.'
                : "Access your dashboard, deliverables, and project progress."}
            </p>

            {isDemo && (
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <Card className="rounded-2xl border-border bg-foreground/5 p-5 text-foreground/70 backdrop-blur-2xl">
                  <div className="flex items-center gap-2 text-sm text-foreground/85">
                    <Shield className="h-4 w-4 text-[var(--brand)]" />
                    RBAC-ready
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/65">
                    Role routing is wired now to avoid refactors later.
                  </p>
                </Card>
                <Card className="rounded-2xl border-border bg-foreground/5 p-5 text-foreground/70 backdrop-blur-2xl">
                  <div className="flex items-center gap-2 text-sm text-foreground/85">
                    <Lock className="h-4 w-4 text-[var(--brand)]" />
                    Tenant-ready
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/65">
                    Data model designed for tenant isolation in backend.
                  </p>
                </Card>
              </div>
            )}
          </div>

          <Card className="rounded-3xl border-border bg-foreground/5 p-6 backdrop-blur-2xl">
            <div className="text-sm font-semibold text-foreground">
              {isDemo ? "Demo sign-in" : "Sign in"}
            </div>
            <p className="mt-2 text-sm text-foreground/65">
              {isDemo ? "Choose a role to preview routing, or sign in with your email." : "Sign in with your email and password to continue."}
            </p>

            <Separator className="my-6 bg-foreground/10" />

            <div className="space-y-4">
              {/* Email + Password Login */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">Email</div>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="dirk@589labs.ai"
                    type="email"
                    data-testid="admin-email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-foreground/55">Password</div>
                  <Input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                    placeholder="Password"
                    type="password"
                    data-testid="admin-password"
                    onKeyDown={(e) => e.key === "Enter" && handleEmailLogin()}
                  />
                </div>
                {loginError && (
                  <div className="text-xs text-red-400 bg-red-950/30 border border-red-800/30 rounded-lg p-2.5">{loginError}</div>
                )}
                <Button
                  data-testid="admin-login-button"
                  onClick={handleEmailLogin}
                  disabled={loginLoading || !email || !password}
                  className="h-12 w-full rounded-none bg-[var(--brand)] text-white hover:bg-[var(--brand)]"
                >
                  {loginLoading ? "Signing in..." : "Sign In"}
                </Button>
              </div>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="bg-foreground/10" />
                </div>
              </div>

              {/* Demo mode: mock sign-in with role selector */}
              {isDemo && (
                <>
                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="bg-foreground/10" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-background/50 px-3 text-xs text-foreground/45">or use demo login</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs uppercase tracking-wide text-foreground/55">Role</div>
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger className="h-11 rounded-none border-border bg-muted text-foreground">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent className="border-border bg-background text-foreground">
                        <SelectItem value="admin">Super Admin</SelectItem>
                        <SelectItem value="consultant">Consultant</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs uppercase tracking-wide text-foreground/55">Display name</div>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-11 rounded-none border-border bg-muted text-foreground placeholder:text-foreground/35"
                      placeholder="Your name"
                    />
                  </div>

                  <Button
                    data-testid="demo-login-button"
                    variant="outline"
                    onClick={async () => {
                      const session = await mockAuth.signIn({ role, full_name: name });
                      toast.success("Signed in (MOCK)", {
                        description: "Session stored locally. Auth is mocked for demo.",
                      });
                      onSignedIn(session);
                    }}
                    className="h-12 w-full rounded-none border-border bg-foreground/5 text-foreground hover:bg-foreground/10"
                  >
                    Demo Sign-in
                  </Button>

                  <div className="text-xs text-foreground/45">
                    Tip: Reset demo data from the profile menu after signing in.
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
